/**
 * 파일명 : pjt0101m.tsx
 * 설명 : 식대포인트 등록 화면
 * 수정내역
 ************************************************
 * Version      수정일자         수정자       수정내용      
 ************************************************
 * 1.0      2023-02-22      이혜린       최초작성
 * 1.1      2023-03-15      임종윤       1차 수정
 * 1.2      2023-04-10      유승현       결재기능추가
 * 1.3      2024-03-26      김세희       식대포인트 조회조건 변경 및 엔터키조회 추가
/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React, { useState } from 'react';

import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Pjt0101mAction } from '../../../redux/slice/pjt/pjt0101m_slice';
import { IInquiryCndsState, IDeptCdItemState, IMealsPointItemState, IUserItemState } from '../../../redux/models/pjt/pjt0101m_model';

/* AntD */
import { Layout, Collapse, Row, Col, Space, Button, Typography, DatePicker, ConfigProvider, Select, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import ExportExcelFile from 'components/exportExcelFile';
import dayjs from 'dayjs';

const { Text } = Typography;

const Pjt0101m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : state 초기 구조선언
     * 설명 : state 초기 구조선언
     */
    const dispatch       = useAppDispatch();
    const inquiryCnds    = useAppSelector((state) => state.Pjt0101m.inquiryCnds);
    const deptCdList     = useAppSelector((state) => state.Pjt0101m.deptCdList);
    const userList       = useAppSelector((state) => state.Pjt0101m.userList);
    const mealsPointList = useAppSelector((state) => state.Pjt0101m.mealsPointList);
    const regAuth        = useAppSelector((state) => state.Pjt0101m.regAuth);
    const current        = useAppSelector((state) => state.Pjt0101m.current);
    const pageSize       = useAppSelector((state) => state.Pjt0101m.pageSize);


    /** Render 변수 선언영역 **/
    /* AntD */
    const { Content } = Layout;
    const { Panel } = Collapse;
    
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        // 사용자 조회
        if (!userList || userList.length < 1) fn_User();

        fn_DeptCds();
        fn_regAuth();
        fn_Search();
    }, []);

    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A',
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Pjt0101mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin() || common.isDeptHdr()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            if (!common.isAdmin() && !common.isDeptHdr()) {
                if (item.empNo === common.getEmpNo()) {
                    arrUserOptions.push({
                        label: item.userNm,
                        value: item.empNo
                    });
                }
            } else {
                arrUserOptions.push({
                    label: item.userNm,
                    value: item.empNo
                });
            }
        });

        return arrUserOptions;
    }, [userList]);

    /**
     * 기능 : fn_DeptCds
     * 설명 : 부서코드 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_DeptCds = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc({deptStCd : 'A'}, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                responseData.deptItems.sort((a:IDeptCdItemState, b:IDeptCdItemState) => (a.deptNm > b.deptNm) ? 1 : -1)

                let arrOptions: IDeptCdItemState[] = [];

                if (common.isAdmin()) {
                    arrOptions.push({label: '전체', value: ''});
                }
        
                responseData.deptItems.forEach((item:IDeptCdItemState) => {
                    arrOptions.push({
                        label: item.deptNm,
                        value: item.deptCd
                    });
                });

                dispatch(Pjt0101mAction.setDeptCdList(arrOptions));
            },
        });
    };

    /**
      * 기능 : 등록권한체크
      * param : 
      **/
    const fn_regAuth = () => {
        /* 관리자의 경우 등록권한 부여 */
        if(common.isAdmin()) {
            dispatch(Pjt0101mAction.setRegAuth(true));
        /* 관리자가 아닌 경우 로그인 사용자가 경영지원부/부서장인지 권한 확인 */
        } else {
            /* 변수선언 및 셋팅 영역 */
            // 입력파라미터

            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc({}, {
                txCd: 'TRPJTR0002',
                success: (responseData:any) => {
                    dispatch(Pjt0101mAction.setRegAuth(responseData.regAuth === 'Y' ? true : false));
                },
            });
        }
    };

    
    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_: any, dateStrings: any) => {
        dispatch(Pjt0101mAction.setInquiryCnds({ ...inquiryCnds, baseYm: dayjs(dateStrings).format('YYYYMM') }));
    };

    
    /**
     *  기능 : 등록여부 콤보 변경
     *  설명 : 등록여부 콤보 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const regYn_onChange = (inputValue:string) => {
        dispatch(Pjt0101mAction.setInquiryCnds({ ...inquiryCnds, regYn: inputValue }));
    }

    /**
     *  기능 : 식대포인트등록내용 투입일/종료일 변경
     *  설명 : 식대포인트등록내용 투입일/종료일 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const inOutDt_onChange = (_:any, dateString:any, record:IMealsPointItemState, type:string) => {
        let baseYm = '';
        let inDt   = '';
        let outDt  = '';
        let newRecord:IMealsPointItemState = {};

        // 투입일의 경우
        if(type === 'in') {
            baseYm = commonDate.maskDate(record.baseYm, 'YYYYMM') + '01';
            inDt   = commonDate.maskDate(dateString   , 'YYYYMMDD');
            outDt  = commonDate.maskDate(record.outDt , 'YYYYMMDD');

            if(!inDt || !outDt) {
                return false;
            }   

            // 투입일이 식대포인트 기준년월 이전의 기간일 경우 기준년월 첫날로 변경
            if(commonDate.getDayDiff(baseYm, inDt) < 0) {
                inDt = baseYm;

            // 투입일이 식대포인트 종료일 이후의 기간일 경우 종료일로 변경    
            } else if(commonDate.getDayDiff(inDt, outDt) < 0){
                inDt = outDt;
            }

        } else if(type === 'out') {
            baseYm = commonDate.maskDate(record.baseYm, 'YYYYMM') + commonDate.fn_LastDateNum(commonDate.maskDate(record.baseYm, 'YYYYMM') + '01');
            inDt   = commonDate.maskDate(record.inDt  , 'YYYYMMDD');
            outDt  = commonDate.maskDate(dateString   , 'YYYYMMDD');

            if(!inDt || !outDt) {
                return false;
            }

            // 종료일이 식대포인트 기준년월 이후의 기간일 경우 기준년월 마지막날로 변경
            if(commonDate.getDayDiff(baseYm, outDt) > 0) {
                outDt = baseYm;

            // 종료일이 식대포인트 시작일 이전의 기간일 경우 투입일로 변경    
            } else if(commonDate.getDayDiff(inDt, outDt) < 0){
                outDt = inDt;
            }

        }

        let difDay = commonDate.getDayDiff(inDt, outDt) + 1;
        let mealsPoint = difDay == 31 ? 40000 : (Math.floor(difDay / 7) + (difDay % 7 >= 3 ? 1 : 0)) * 10000;

        if(type === 'in') {
            newRecord = { ...record, inDt: inDt, mealsPoint: mealsPoint };
        } else if(type === 'out') {
            newRecord = { ...record, outDt: outDt, mealsPoint: mealsPoint };
        }

        dispatch(Pjt0101mAction.setMealsPointItem(newRecord));
    }

    
    /**
      * 기능 : 초기화 함수
      * param : 
      **/
    const fn_Reset = () => {
        dispatch(Pjt0101mAction.initState());
    };

    /**
      * 기능 : 검색 함수
      * param : 
      **/
    const fn_Search = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0001',
            success: (responseData:any) => {
                dispatch(Pjt0101mAction.setMealsPointList(responseData.mealsPointList));
            },
        });
    };

    /**
      * 기능 : 등록 함수
      * param : 
      **/
    const fn_Reg = () => {
        /* 등록할 식대포인트 내역이 없을 경우 */
        if (mealsPointList.length === 0) {
            const param = {
                title: '위드정보 확인',
                contents: '등록내용이 없습니다.',
            };
            common.openInfo(param);
            return;
        }
        

        const param = {
            title : '위드정보 확인',
            contents: '등록처리 하시겠습니까?',
            accept : () => {
                /* 변수선언 및 셋팅 영역 */
                const requestData = {
                    // 입력파라미터
                    ...inquiryCnds,
                    mealsPointList: mealsPointList,
                };
        
                commonProxy.callAsyncSvc(requestData, {
                    txCd : 'TRPJTS0001',
                    success : (responseData:any) => {
                        const param = {
                            title : '위드정보 확인',
                            contents: '등록 처리 되었습니다.',
                        };
        
                        common.openAlert(param);

                        dispatch(Pjt0101mAction.setMealsPointList(responseData.mealsPointList));
                    },
                });
            }
        };
        common.openConfirm(param);
    };

    /**
         * 기능 : 입력 후 Enter key 입력 시 조회
         * 설명 : 입력컴포넌트
         * param : 
         * return : None
         */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

     /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
     const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_reset': {
                fn_Reset();
                break;
            }

            // 등록버튼
            case 'btn_reg': {
                fn_Reg();
                break;
            }

            // 조회버튼
            case 'btn_search': {
                fn_Search();
                break;
            }

            default:
        }
    };

    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>기준년월</div>,
                dataIndex: 'baseYm',
                key: 'baseYm',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM'),
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서명</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '15%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트명</div>,
                dataIndex: 'pjNm',
                key: 'pjNm',
                align: 'left',
                width: '30%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트PM</div>,
                dataIndex: 'pjPmNm',
                key: 'pjPmNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>등록여부</div>,
                dataIndex: 'regYn',
                key: 'regYn',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>투입일</div>,
                dataIndex: 'inDt',
                key: 'inDt',
                align: 'center',
    
                render: (text:string, record:IMealsPointItemState) => (
                    <DatePicker
                        name='inDt'
                        value={common.isEmptyString(text) ? null : dayjs(text)}
                        picker='date'
                        onChange={(data, dateString) => {inOutDt_onChange(data, dateString, record, 'in')}}
                    >
                    </DatePicker>
                ),
    
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>종료일</div>,
                dataIndex: 'outDt',
                key: 'outDt',
                align: 'center',
                render: (text:string, record:IMealsPointItemState) => (
                    <DatePicker
                        name='outDt'
                        value={common.isEmptyString(text) ? null : dayjs(text)}
                        picker='date'
                        onChange={(data, dateString) => {inOutDt_onChange(data, dateString, record, 'out')}}
                    >
                    </DatePicker>
                ),
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>식대포인트</div>,
                dataIndex: 'mealsPoint',
                key: 'mealsPoint',
                render: (text:string) => text.toLocaleString(),
                align: 'right',
                width: '15%',
            },];
            return arr;
    }, []);

    /************************************************
     * 개발자 함수 영역  
     ************************************************/
    /* 엑셀다운로드 */
    const excelParams = {
        datas: mealsPointList,
        btnName: '엑셀파일 내려받기',
        fileName: '식대포인트현황_' + inquiryCnds.baseYm,
        sheetName:'식대포인트현황_' + inquiryCnds.baseYm,
        columns: columns(),
    }

    return (
        /* 화면 디자인 영역 */
        <Layout>
            <Content>
                <Collapse
                    bordered={false}
                    defaultActiveKey={['1']}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className='site-collapse-custom-collapse'
                >
                    <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                        <Row
                            align='middle'
                            justify='space-between'
                            style={{ marginTop: 1 }}
                        >
                            <Col flex='9.5'>
                                <Space>
                                    <Space direction='horizontal'>
                                        <label>기준년월</label>
                                        <ConfigProvider locale={locale}>
                                            <DatePicker 
                                                style={{ width: 120 }}
                                                id='tx_calendar'
                                                allowClear={false}
                                                onChange={calendar_onChange}
                                                onKeyUp={handleOnKeyUp}
                                                value={dayjs(inquiryCnds.baseYm)}
                                                picker='month'
                                            />
                                        </ConfigProvider>
                                    </Space>
                                    <Space direction='horizontal'>
                                        <label>부서명</label>
                                        <Select
                                            style={{
                                                width: 200,
                                            }}
                                            optionFilterProp='label'
                                            options={deptCdList}
                                            onKeyUp={handleOnKeyUp}
                                            onChange={val => {
                                                dispatch(Pjt0101mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                            }}
                                            value={inquiryCnds.deptCd}
                                        />
                                    </Space>
                                    <Space direction='horizontal'>
                                        <label>직원명</label>
                                        <Select 
                                            showSearch
                                            style={{
                                                width: 120,
                                            }}
                                            defaultValue=''
                                            optionFilterProp='label'
                                            filterSort={(optionA: any, optionB: any) => {
                                                const labelA = (optionA?.label ?? '').toLowerCase();
                                                const labelB = (optionB?.label ?? '').toLowerCase();
                                                
                                                if (labelA === '전체') return -1;
                                                if (labelB === '전체') return 1;
                                                
                                                return labelA.localeCompare(labelB);
                                            }}
                                            options={userOptions()}
                                            onKeyUp={handleOnKeyUp}
                                            value={inquiryCnds.empNo}
                                            onChange={val => dispatch(Pjt0101mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                            // ReadOnly 적용
                                            {...common.getReadonlySelectboxProps(!common.isAdmin() && !common.isDeptHdr())}
                                        ></Select>
                                    </Space>
                                    <label>등록여부</label>
                                    <Select id='inputRegYn' style={{ width: 100}} value={inquiryCnds.regYn} onChange={regYn_onChange}>
                                        <option value=''>전체</option>
                                        <option value='N'>부</option>
                                        <option value='Y'>여</option>
                                    </Select>
                                </Space>
                            </Col>
                            <Col flex='0.5'>
                                <Space direction='horizontal'>
                                    <Button
                                        name='btn_reset'
                                        className='background-color-default-button'
                                        onClick={onClick}
                                        style={{ marginTop: 1 }}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        name='btn_search'
                                        className='background-color-primary-button'
                                        icon={<SearchOutlined />}
                                        onClick={onClick}
                                        style={{ marginTop: 1 }}
                                        size='middle'
                                    >
                                        조회
                                    </Button>
                                    {regAuth? 
                                        <Button
                                            name='btn_reg'
                                            className='background-color-primary-button'
                                            onClick={onClick}
                                        >
                                            등록
                                        </Button> : null
                                    }                                  
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>
                <Row align='middle' style={{ marginBottom: 10, marginTop: 5 }}>
                    <Col flex='9.5'>
                        <Space>
                            <img className='title-icon' alt='' />
                            <Text strong>식대포인트 등록내용</Text>
                            {!regAuth ? <Text strong className='label-required'>관리자/경영지원부/부서장만 등록 가능합니다.</Text> : null}
                        </Space>
                    </Col>
                    <Col flex='0.5' style={{ marginTop: 5 }}>
                        <ExportExcelFile excelParams={excelParams}/>
                    </Col>
                </Row>
                <Table
                    dataSource={mealsPointList}
                    key='empNo'
                    columns={columns()}
                    size='small'
                    style={{width:'100%'}}
                    scroll={{y:550}}
                    pagination={{
                        pageSize: pageSize,
                        current: current,
                        showTotal : () => '총 ' + mealsPointList.length + ' 건',
                        onChange: (page, pageSize) => {
                            dispatch(Pjt0101mAction.setCurrent(page));
                            dispatch(Pjt0101mAction.setPageSize(pageSize));
                        },
                    }}
                />
            </Content>
        </Layout>
    );
}

export default Pjt0101m;